import type { SalesforceProduct } from "@shared/services";
import type { UserProfileCountryResponse } from "@shared/types";
import * as T from "@shared/types";
import { Service } from "@shared/types";
import { SuccessResponse } from "@shared/types/apiResponse";
import { AxiosRequestConfig } from "axios";
import { axios } from ".";
import { DEFAULT_VENDOR_LOCALE } from "../../providers/vendor/actions";
import { throwError } from "./utilities/throwing";

export interface VendorImpersonateResponse {
  vendor: T.Vendor;
  accountType: T.AccountType;
}

export type ShopifyAuthInput =
  | { authMethod: "accessToken"; accessToken: string }
  | { authMethod: "clientCredentials"; clientId: string; clientSecret: string };

export interface ShopifyOAuthCompletionInput {
  authMethod: "oauthCompletion";
  code: string;
  state: string;
  encryptedCreds: string;
}

export interface ShopifyAuthorizePrepareResponse {
  state: string;
  encryptedCreds: string;
  authorizationUrl: string;
}

export const SERVICE_BASE_URL = "/api/v1/services/bc-services";
export const PRODUCT_BASE_URL = "/api/v1/products";
export const VENDORS_URL = `${SERVICE_BASE_URL}/vendors`;
export const VENDOR_IMPERSONATE_URL = `${VENDORS_URL}/impersonate`;

export const getServices = async (serviceType?: Service["serviceType"]) => {
  const config = { params: { serviceType } };
  const response = await axios.get<{ data: Service[] }>(SERVICE_BASE_URL, config);
  return response.data.data;
};

export const getVendors = async () => {
  // The roaster list is fetched without a selected vendor, so the backend
  // returns 403 when no vendorKey is present. Opt this request out of the
  // auth-error→logout pipeline so the 403 surfaces to the caller instead of
  // logging the Roaster Admin out.
  const response = await axios.get<{ data: T.BCCVendor[] }>(VENDORS_URL, { skipAuthLogout: true });
  return response.data.data;
};

export const getVendorForImpersonation = async (vendorKey: string): Promise<VendorImpersonateResponse> => {
  const response = await axios.get<{ data: VendorImpersonateResponse }>(VENDOR_IMPERSONATE_URL, { headers: { vendorKey } });
  return response.data.data;
};

export type InstallShopifyServiceParams =
  | ({ shop: string; locale?: string } & ShopifyAuthInput)
  | ({ shop: string; locale?: string } & ShopifyOAuthCompletionInput);

export const installShopifyService = async (params: InstallShopifyServiceParams) => installService("shopify", params);

export const startShopifyAuthorize = async (params: {
  shop: string;
  clientId: string;
  clientSecret: string;
  locale?: string;
  targetHost?: string;
}): Promise<ShopifyAuthorizePrepareResponse> => {
  const url = `${SERVICE_BASE_URL}/shopify/authorize-prepare`;
  try {
    const response = await axios.post<{ data: ShopifyAuthorizePrepareResponse }>(url, params);
    return response.data.data;
  } catch (e) {
    throwError("Start Shopify authorize error", e);
    throw e;
  }
};

export const installWooCommerceService = async (shop: string, locale?: string) => {
  const params = { shop, locale };
  return await installService("woocommerce", params);
};

const installService = async (serviceId: T.ServiceId, params: object) => {
  const url = `${SERVICE_BASE_URL}/${serviceId}/install`;
  try {
    const response = await axios.post(url, params);
    return response.data.data;
  } catch (e) {
    throwError("Install service error", e);
  }
};

// installationId is omitted for vendors with no Shopify installation (e.g.
// ShipStation roasters) — the backend then falls back to a vendorKey-only scope.
const buildSkuImportDraftUrl = (installationId?: string) =>
  installationId ? `${PRODUCT_BASE_URL}/sku-import-draft/${installationId}` : `${PRODUCT_BASE_URL}/sku-import-draft`;

export const getSkuImportDrafts = async (installationId?: string) => {
  const url = buildSkuImportDraftUrl(installationId);
  const response = await axios.get<T.SkuImportDraftEntry[]>(url);
  return response.data;
};

export const postSkuImportDrafts = async (vendor: T.Vendor, installationId: string | undefined, products: T.FormProduct[]) => {
  const url = buildSkuImportDraftUrl(installationId);
  if (!vendor.contactId) {
    return;
  }
  try {
    return await axios.post<SalesforceProduct[], SuccessResponse>(
      url,
      setToDraftObject(sanitizeSkuImportDrafts(products), vendor.contactId, vendor.firstName + " " + vendor.lastName)
    );
  } catch (e) {
    throwError("Error in posting sku import drafts", e);
  }
};

export const patchSkuImportDrafts = async (
  vendor: T.Vendor,
  installationId: string | undefined,
  product: T.FormProduct,
  status: T.SkuImportDraftState
) => {
  const config: AxiosRequestConfig = { params: { state: status, id: product.id } };
  const url = buildSkuImportDraftUrl(installationId);
  if (!vendor.contactId) {
    return;
  }
  const skuImportDraftEntry = {
    ...setToDraftObject([product], vendor.contactId, vendor.firstName + " " + vendor.lastName)[0],
    state: status,
  };
  try {
    return await axios.patch<SalesforceProduct[], SuccessResponse>(url, skuImportDraftEntry, config);
  } catch (e) {
    throwError("Error in patching sku import drafts", e);
  }
};

// prepare the object to be sent to the server
const setToDraftObject = (products: T.FormProduct[], vendorContactId: string, vendorUserName: string) => {
  return products.map((product) => {
    return {
      draftFormProduct: product,
      id: product.id,
      type: "skuImportDraft",
      vendorContactId,
      vendorUserName,
    };
  });
};

export const sanitizeSkuImportDrafts = (drafts: T.FormProduct[]) => {
  return drafts.map((draft) => {
    return {
      ...draft,
      isProductFormValid: false,
      imageUploads: undefined,
      variants: sanitizeSkuImportDraftVariants(draft.variants),
    };
  });
};

// sanitize the draft variants so that some values do not clash on the way back
export const sanitizeSkuImportDraftVariants = (draftVariants: T.FormVariant[]) => {
  return draftVariants.map((variant) => {
    return {
      ...variant,
      status: undefined,
    };
  });
};

export const getActivities = async (subjectId?: string, lastId?: string, lastCreatedAt?: string, limit?: number) => {
  const url = `${SERVICE_BASE_URL}/activities`;
  const config = { params: { subjectId, lastId, lastCreatedAt, limit } };
  const response = await axios.get<{ data: T.ActivityResult }>(url, config);
  return response.data.data;
};

export const getUserLocale = async (accessToken: string) => {
  const url = `${SERVICE_BASE_URL}/user-locale`;
  const config: AxiosRequestConfig = { params: { token: accessToken } };
  const response = await axios.get<{ data: T.Locale }>(url, config);
  return response.data.data ?? DEFAULT_VENDOR_LOCALE;
};

export const getCustomerAccountCarrierDetails = async () => {
  const url = `${SERVICE_BASE_URL}/customer-carrier-account`;
  const response = await axios.get<{ data: T.CarrierCustomerAccountBinding[] }>(url);
  return response.data.data;
};

export const getCountries = async (): Promise<UserProfileCountryResponse> => {
  const url = `${SERVICE_BASE_URL}/countries`;
  const response = await axios.get<{ data: UserProfileCountryResponse }>(url);
  return response.data.data;
};

export const setVendorProperties = async (properties: T.VendorProperties) => {
  const url = `${SERVICE_BASE_URL}/vendor-preferences`;
  try {
    const response = await axios.post<T.VendorProperties>(url, properties);
    return response.data;
  } catch (e) {
    throwError("Error in setting vendor preferences", e);
  }
};
